/**
 * FO 커머스 헤더 네비게이션 선언 SSoT (IA 03-정보구조-IA §FO 네비게이션).
 *
 * 정적 SPA — DB 메뉴 아님. 헤더를 선언적으로 정의해 마크업 수정 없이 배열 편집만으로
 * 확장한다(IA 03:222 "하드코딩 금지"). AppHeader 는 이 배열만 읽어 데스크톱 드롭다운과
 * 모바일 아코디언 양쪽을 렌더한다.
 *
 * ## 2계층 구조 (#118 — 멀티 메뉴 전환)
 *
 * 기존 단순 나열형(1계층 `NavItem[]`)을 **대분류(NavGroup) → 하위 항목(NavLink)** 2계층으로
 * 전환했다. 과거에는 섹션 앵커(#sample·#price 등)를 AppHeader 가 별도 `sections` 상수로
 * 직접 소유해 "메뉴 정의가 두 곳"이었는데, 앵커를 NavLink 의 한 종류(`section`)로 흡수해
 * **헤더에 뜨는 모든 것이 이 파일 하나**에서 나오도록 일원화했다.
 *
 * ## 전폭 메가메뉴 전환
 *
 * 데스크톱은 대분류별 개별 드롭다운(320px)에서 **전폭 패널 1장에 모든 대분류를 컬럼으로
 * 동시 노출**하는 형태로 바뀌었다 — 대분류 수만큼 hover 를 반복해야 전체가 보이던 동선 불편
 * 때문. 이 파일의 **구조는 그대로**고(AppHeader 가 렌더 방식만 바꿔 읽는다), 대신 컬럼 폭에
 * 맞춰 `desc` 를 짧게 쓰는 규약과 그룹 단위 `hidden` 토글이 추가됐다.
 *
 * ## 링크 종류 (NavLink 의 목적지 — 셋 중 정확히 하나)
 *
 * | 필드      | 의미                    | AppHeader 처리                          |
 * |-----------|-------------------------|-----------------------------------------|
 * | `route`   | 내부 라우트             | `<RouterLink>`                          |
 * | `href`    | 외부 URL                | `<a target="_blank" rel="noopener">`    |
 * | `section` | 특정 페이지의 섹션 앵커 | `useScrollTo().goToSection(path, hash)` |
 *
 * ⚠️ **프리렌더(공개·색인) 경로의 `route` 는 trailing slash 정규형**(`/guide/`·`/materials/`)으로 쓴다 —
 * 슬래시가 없으면 크롤러가 헤더 링크를 따라갈 때마다 nginx 디렉토리 301 을 태운다(근거: brand.ts
 * `canonicalUrl`). CSR 전용 경로(`/analysis`·`/mypage/*`·`/login` 등)는 프리렌더 파일이 없어 대상이 아니다.
 * 활성 밑줄 판정(AppHeader `isGroupActive`)은 양쪽 표기를 정규화해 비교하므로 표기 차이에 영향받지 않는다.
 *
 * `disabled: true` 는 목적지를 무시하고 비활성 표시(링크 아님) — 준비중 기능용.
 *
 * auth 노출 규칙:
 *   - 'always': 모든 인증 수준 노출
 *   - 'guest' : 비로그인(guest)만
 *   - 'authed': 로그인(pending|verified)만
 */
import { brand } from '#/config/brand';

/** 항목 노출 인증 수준. */
export type NavAuth = 'always' | 'guest' | 'authed';

/** 하위 항목 우측 상태 배지 톤 — 'soon'(준비중)만 회색, 나머지는 골드/뉴트럴. */
export type NavBadgeTone = 'ready' | 'soon' | 'read';

/** 상태 배지 — 텍스트 + 톤. */
export interface NavBadge {
  /** 표시 문구 — 예: '이용 가능' · '준비중' · '읽기 6분'. */
  text: string;
  /** 색 톤. */
  tone: NavBadgeTone;
}

/** 섹션 앵커 목적지 — 앵커가 존재하는 라우트 + 해시. */
export interface NavSection {
  /** 앵커가 속한 라우트 경로 — 예: '/'. */
  path: string;
  /** '#sample' 형태의 해시. */
  hash: string;
}

/** 드롭다운 패널 안의 하위 항목 1건. */
export interface NavLink {
  /** 표시 라벨. */
  label: string;
  /** 한 줄 설명(패널에서 라벨 아래 표시). */
  desc: string;
  /** 노출 인증 수준. */
  auth: NavAuth;
  /** 내부 route(vue-router). */
  route?: string;
  /** 외부 링크 URL(target=_blank). */
  href?: string;
  /** 섹션 앵커 목적지. */
  section?: NavSection;
  /** 상태 배지. */
  badge?: NavBadge;
  /** 준비중 등으로 진입 차단 — 목적지를 무시하고 비활성 표시. */
  disabled?: boolean;
}

/** 헤더 대분류 1건 — hover·클릭으로 패널을 여는 단위. */
export interface NavGroup {
  /** 대분류 라벨. */
  label: string;
  /** 정렬 순서(오름차순). */
  order: number;
  /**
   * true 면 헤더에서 숨긴다(데스크톱 메가메뉴·모바일 아코디언 공통).
   *
   * 배열에서 지우지 않는 이유 — **숨김/복원을 이 한 줄 토글로 끝내기 위해서다.**
   * 과거처럼 그룹을 통짜 주석 처리하면 그 그룹만 쓰던 헬퍼가 dead code 가 되어
   * `no-unused-vars`(CI error)까지 함께 주석해야 했고, 복원 시 짝을 놓치기 쉬웠다.
   * 데이터 필드로 두면 참조가 살아 있어 그 문제가 아예 생기지 않고,
   * 그룹 정의(route·desc·badge)도 그대로 보존된다.
   */
  hidden?: boolean;
  /**
   * 대분류 라벨 옆 보조 칩(예: 'AI') — 모바일 메뉴 아코디언에서 라벨 뒤에 붙는다.
   * 라벨은 학생 눈높이 용어로 두고(용어집), 기능 정체는 칩으로 보조 신호만 준다.
   */
  chip?: string;
  /** 하위 항목. */
  items: NavLink[];
}

/**
 * 헤더 우측 유틸리티(대분류 밖) — 로그인·회원가입·장바구니·마이페이지·로그아웃.
 * 드롭다운을 갖지 않는 단일 액션이라 NavGroup 과 분리한다.
 */
export interface NavUtilItem {
  /** 표시 라벨. */
  label: string;
  /** 노출 인증 수준. */
  auth: NavAuth;
  /** 정렬 순서(오름차순). */
  order: number;
  /** 내부 route(vue-router). external·action 항목엔 없음. */
  route?: string;
  /** 외부 링크 URL(external=true 항목). */
  href?: string;
  /** 외부 링크 여부(target=_blank). */
  external?: boolean;
  /** 액션 항목(route 없음) — logout 등. AppHeader 가 kind 로 핸들러 분기. */
  kind?: 'logout';
  /** 장바구니 뱃지 표시(cart.count). */
  badge?: 'cart';
  /** 아이콘 식별자(AppHeader 가 컴포넌트 매핑). */
  icon?: 'cart';
  /** 골드 강조 버튼으로 표시(회원가입 등). */
  emphasis?: boolean;
}

/**
 * 헤더 대분류 SSoT. AppHeader 가 `hidden`·auth 로 필터해 데스크톱 메가메뉴와
 * 모바일 아코디언 양쪽에 v-for. 하위 항목이 전부 숨겨진 대분류는 렌더하지 않는다(빈 패널 방지).
 *
 * ## desc 는 짧게
 *
 * 메가메뉴 컬럼 폭이 ~200px 라 desc 가 길면 4줄로 접혀 컬럼 높이가 들쭉날쭉해진다.
 * **10~20자**를 상한으로 쓴다(긴 설명은 각 페이지 본문이 담당 — GNB 는 이정표일 뿐).
 *
 * ## 워크스페이스(2차) route 를 리터럴로 적는 이유
 *
 * `brand.crossLinks.analysis` 는 **서브도메인 분리 대비로 `/analysis` 홈 하나**를 가리키는
 * 값이라 `/analysis/tests` 같은 하위 화면을 표현할 수 없다(템플릿 접합은 SSoT 가 아니라
 * 절대 URL 로 바뀌는 순간 깨진다). `data/analysis/workspaceNav.ts` 참조도 부적합 —
 * 그 배열은 `crumbsForRoute()` 가 route 로 역추적하는 구조라 결합이 생기면
 * 워크스페이스 브레드크럼이 조용히 깨진다. 커머스 GNB 의 다른 모든 항목이 이미
 * 리터럴 방식이므로 여기서도 일관되게 리터럴로 둔다.
 */
export const HEADER_GROUPS: readonly NavGroup[] = [
  {
    label: '리포트',
    order: 10,
    items: [
      {
        label: '대학 검색',
        desc: '전국 120여 개 대학',
        auth: 'always',
        route: '/materials/',
      },
      {
        label: '리포트 샘플',
        desc: '실제 내지 미리보기',
        auth: 'always',
        section: { path: '/', hash: '#sample' },
      },
      // 면접 로드맵(#339) — 구매 화면이 아니라 랜딩 소개 섹션으로 보낸다. 헤더는 정적 선언이라 판매 상태를 모르는데,
      // 구매 화면은 판매 중지 시 404 안내가 뜬다. 소개 섹션은 항상 있고 그 안의 구매 CTA(CatalogEntryButton)가
      // 판매 상태를 따른다 — 상품군 구매 링크는 카탈로그 게이트를 거친다는 규칙(useCatalogGroupEntry)과 같다.
      {
        label: '면접 로드맵',
        desc: '계열별 면접 준비 PDF',
        auth: 'always',
        section: { path: '/', hash: '#product-interview' },
      },
      {
        label: '가격 안내',
        desc: '단권·패키지 가격',
        auth: 'always',
        section: { path: '/', hash: '#price' },
      },
    ],
  },
  // 구 'AI 입시분석' — FO 노출 용어는 학생·학부모 눈높이로 간다(docs/fo-service-glossary.md).
  // 학생은 'AI' 가 아니라 '내 성적'에 반응한다.
  {
    label: '내 성적 분석',
    order: 20,
    chip: 'AI',
    // 2026-07-30 3차 미팅에서 1차 오픈 범위를 PDF 리포트 커머스로 한정해 숨겼다가 2026-09-07 노출,
    // 2026-09-23~10-13 은 빌드 변수(`VITE_ANALYSIS_MENU`)로 dev 만 노출했고, 2026-10-13 운영 오픈과 함께
    // 변수 게이트를 걷어 **전 환경 상시 노출**이다. 다시 숨겨야 하면 `hidden: true` 한 줄이면 된다.

    // ## 워크스페이스 5축을 미러링하지 않는다 (IA 역할 분리)
    //
    // 커머스 GNB 는 "가치 제안" 시작점만 큐레이션하는 **관문**이고, 전체 구조(내 입시카드·
    // AI 종합분석·리포트 보관함 포함 5축)는 워크스페이스 GNB(`data/analysis/workspaceNav.ts`)가
    // 단독 소유한다. 두 벌을 1:1 로 맞추면 드리프트가 구조적으로 반복된다(수시 분석이
    // 여기만 '준비중'으로 남았던 사례). 도구성 축(입시카드·보관함)은 진입 전 사용자에게
    // 의미가 없어 여기선 노출하지 않는다 — 하위 전체는 '전체 보기' 항목이 안내한다.
    items: [
      {
        label: '정시 분석',
        desc: '백분위로 합격권 진단',
        auth: 'always',
        route: '/analysis/jeongsi',
      },
      {
        label: '수시 분석',
        desc: '교과등급으로 지원선 진단',
        auth: 'always',
        route: '/analysis/susi',
      },
      // 077 비로그인 샘플 뷰 — 클릭 시 검사 4종 소개·결과 발췌가 열리고(로그인 화면 아님),
      // [시작하기]에서만 로그인을 요구한다. 정시·수시도 같은 규칙.
      {
        label: '진로심리검사',
        desc: '진로·학습·심리·진학 4종',
        auth: 'always',
        route: '/analysis/tests',
      },
      // 관문의 마지막 항목 — 나머지 축(내 입시카드·AI 종합분석·리포트 보관함)은
      // 워크스페이스 홈의 5축 카드가 소개한다.
      {
        label: '분석 홈 전체 보기',
        desc: '모든 분석 기능 한눈에',
        auth: 'always',
        route: '/analysis',
      },
    ],
  },
  {
    label: '입시 가이드',
    order: 30,
    items: [
      // 원서 접수 전 반드시 확인해야 하는 규정이라 전략편보다 앞에 둔다.
      {
        label: '수시 6회 규정',
        desc: '6회는 대학이 아니라 전형 기준',
        auth: 'always',
        route: '/guide/susi-6-rule/',
        badge: { text: '읽기 5분', tone: 'read' },
      },
      {
        label: '수시 전략',
        desc: '고3 3point 지원 전략',
        auth: 'always',
        route: '/guide/susi-strategy/',
        badge: { text: '읽기 6분', tone: 'read' },
      },
      {
        label: '면접 준비',
        desc: '기출 분석·답변 8원칙',
        auth: 'always',
        route: '/guide/interview/',
        badge: { text: '읽기 7분', tone: 'read' },
      },
      {
        label: '자기소개서',
        desc: '문항 확인부터 퇴고까지',
        auth: 'always',
        route: '/guide/self-introduction/',
        badge: { text: '읽기 8분', tone: 'read' },
      },
      {
        label: '전체 보기',
        desc: '가이드 전체 목록',
        auth: 'always',
        route: '/guide/',
      },
    ],
  },
  {
    label: '합격 사례',
    order: 40,
    items: [
      {
        label: '합격 사례',
        desc: '상담 학생 합격 결과',
        auth: 'always',
        section: { path: '/', hash: '#reviews' },
      },
      {
        label: '이용 후기',
        desc: '먼저 이용한 분들의 이야기',
        auth: 'always',
        route: '/review',
      },
      {
        label: '전문가 소개',
        desc: '방송 140회·저서 7권',
        auth: 'always',
        section: { path: '/guide', hash: '#authority' },
      },
    ],
  },
  {
    label: '고객 지원',
    order: 50,
    items: [
      {
        label: '입시 소식',
        desc: '대입 공식 발표 해설',
        auth: 'always',
        route: '/news/',
      },
      {
        label: '공지사항',
        desc: '서비스 안내·업데이트',
        auth: 'always',
        route: '/notice',
      },
      {
        label: '자주 묻는 질문',
        desc: '주문·결제·환불 안내',
        auth: 'always',
        section: { path: '/', hash: '#faq' },
      },
      {
        label: '컨설팅 문의',
        desc: '1:1 입시 상담',
        auth: 'always',
        href: brand.consultUrl,
      },
      {
        label: '내 문의 내역',
        desc: '문의·답변 확인',
        auth: 'authed',
        route: '/mypage/inquiries',
      },
      {
        label: '이용약관',
        desc: '약관·개인정보처리방침',
        auth: 'always',
        route: '/legal/terms/',
      },
    ],
  },
] as const;

/**
 * 헤더 우측 유틸리티 SSoT — 대분류와 위계를 분리한 단일 액션.
 * (로그인/회원가입은 guest, 장바구니/마이페이지/로그아웃은 authed)
 */
export const HEADER_UTILS: readonly NavUtilItem[] = [
  { label: '로그인', route: '/login', auth: 'guest', order: 10 },
  { label: '회원가입', route: '/signup', auth: 'guest', order: 20, emphasis: true },
  {
    label: '장바구니',
    route: '/packages',
    auth: 'authed',
    order: 30,
    badge: 'cart',
    icon: 'cart',
  },
  // 마이페이지 진입은 첫 탭(내 자료실) — 구 '내 정보'는 #167 로 폐기됐다.
  { label: '마이페이지', route: '/mypage/library', auth: 'authed', order: 40 },
  { label: '로그아웃', kind: 'logout', auth: 'authed', order: 50 },
] as const;
